import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import { Box, Dialog, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { EDistributionChannelType } from '../../../../../types/distribution-channel'
import CircularLoading from '../../../../../components/CircularProgress'
import { IAddress, IAddressThailand } from '../../../../../types/address'
import SelectSearch, { IOptionSelect } from '../../../../../components/SelectSearch'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IAddressFormModalProps {
  isOpen: boolean;
  onClose: (visible: boolean) => void;
  address?: IAddress;
  onUpdateAddressListSuccess: () => void;
}

interface IAddressFormModal {
  phoneNumber: string;
  address: string;
  subDistrict: string;
  district: string;
  name: string;
  province: string;
  postcode: string;
}

const menuItemsType = [
  {
    label: 'Facebook',
    value: EDistributionChannelType.FACEBOOK,
  },
  {
    label: 'Tiktok',
    value: EDistributionChannelType.TIKTOK,
  },
  {
    label: 'Instagram',
    value: EDistributionChannelType.INSTAGRAM,
  },
  {
    label: 'Shopee',
    value: EDistributionChannelType.SHOPEE,
  },
  {
    label: 'Lazada',
    value: EDistributionChannelType.LAZADA,
  },
  {
    label: 'อื่นๆ',
    value: EDistributionChannelType.ETC,
  },
]

const getAddressFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อ'),
    phoneNumber: Yup.string().required('กรุณากรอกเบอร์โทรศัพท์'),
    address: Yup.string().required('กรุณากรอกที่อยู่'),
    subDistrict: Yup.string().required('กรุณากรอกแขวง/ตำบล'),
    district: Yup.string().required('กรุณากรอกเขต/อำเภอ'),
    province: Yup.string().required('กรุณากรอกจังหวัด'),
    postcode: Yup.string().required('กรุณากรอกรหัสไปรษณีย์'),
  })
}

export const AddressFormModal = (props: IAddressFormModalProps) => {
  const { isOpen, onClose, address, onUpdateAddressListSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchAddress, setSearchAddress] = useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>("")

  useEffect(() => {
    setIsLoading(true)
    formik.setValues(
      {
        name: address?.name || '',
        phoneNumber: address?.phoneNumber || '',
        address: address?.address || '',
        subDistrict: address?.subDistrict || '',
        district: address?.district || '',
        province: address?.province || '',
        postcode: address?.postcode || '',
      },
    )
    setIsLoading(false)
  }, [isOpen, address])

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  const formik = useFormik<IAddressFormModal>({
    initialValues: {
      name: '',
      phoneNumber: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      postcode: '',
    } as IAddressFormModal,
    validationSchema: getAddressFormValidationSchema(),
    onSubmit: async (values: IAddressFormModal) => {
      setIsLoading(true)
      const isUpdate = !!address
      try {
        const response = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/address/${address.objectId}` : '/address',
          data:
          {
            ...values,
          },
        })
        const { status } = response
        message.success(status === 201 ? `เพิ่มที่อยู่ ${values?.address} สำเร็จ` : `แก้ไขที่อยู่ ${values?.address} สำเร็จ`)
        formik.resetForm()
        onClose(false)
        onUpdateAddressListSuccess()
        setResetAutoComplete(new Date().getTime().toString())
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>{address ? 'แก้ไขที่อยู่' : 'เพิ่มที่อยู่'}</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อ"
                name="name"
                helperText={
                  formik.touched.name && formik.errors.name as string
                }
                error={
                  !!(formik.touched.name && formik.errors.name)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="address"
                fullWidth
                label="ที่อยู่"
                name="address"
                helperText={
                  formik.touched.address && formik.errors.address as string
                }
                error={
                  !!(formik.touched.address && formik.errors.address)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.address}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <SelectSearch<IAddressThailand>
                resetAutoComplete={resetAutoComplete}
                label={'ค้นหาที่อยู่'}
                onSelect={(e) => {
                  formik.setFieldValue('subDistrict', e?.value.subDistrict)
                  formik.setFieldValue('district', e?.value.district)
                  formik.setFieldValue('province', e?.value.province)
                  formik.setFieldValue('postcode', e?.value.zipcode)
                }}
                search={searchAddress}
                onSearch={handleSearchAddress}
                options={
                  addressList.data?.map((address: IAddressThailand) => ({
                    label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                    value: address,
                  })) || [] as IOptionSelect<IAddressThailand>[]
                } />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="subDistrict"
                fullWidth
                label="แขวง/ตำบล"
                name="subDistrict"
                helperText={
                  formik.touched.subDistrict && formik.errors.subDistrict as string
                }
                error={
                  !!(formik.touched.subDistrict && formik.errors.subDistrict)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.subDistrict}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="district"
                fullWidth
                label="อำเภอ"
                name="district"
                helperText={
                  formik.touched.district && formik.errors.district as string
                }
                error={
                  !!(formik.touched.district && formik.errors.district)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.district}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="province"
                fullWidth
                label="จังหวัด"
                name="province"
                helperText={
                  formik.touched.province && formik.errors.province as string
                }
                error={
                  !!(formik.touched.province && formik.errors.province)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.province}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="postcode"
                fullWidth
                label="รหัสไปรษณีย์"
                name="postcode"
                helperText={
                  formik.touched.postcode && formik.errors.postcode as string
                }
                error={
                  !!(formik.touched.postcode && formik.errors.postcode)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.postcode}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="phoneNumber"
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phoneNumber"
                helperText={
                  formik.touched.phoneNumber && formik.errors.phoneNumber as string
                }
                error={
                  !!(formik.touched.phoneNumber && formik.errors.phoneNumber)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.phoneNumber}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {address ? 'แก้ไขที่อยู่' : 'เพิ่มที่อยู่'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
